/**
 * TabFrame (FOUNDATION — v5 "PIXEL SWEETHEART") — the protected app frame:
 * auth gate + warm-glass top bar (pixel wordmark + cracked pixel heart) +
 * bouncy bottom tab bar (deck / menu / likes / matches / store / me) +
 * animated <Outlet/> for the feature routes registered in src/router.tsx.
 *
 * v5: mounts the shared atmosphere once for every authenticated screen —
 * <AmbientBackground/> (candy glow + floating pixel hearts) and
 * <GrainOverlay/> (a whisper of grain, frozen under reduced-motion). Soft
 * camera-move route transitions (pageVariants), a sliding candy tab pill
 * (framer layoutId), a JELLY icon bounce on activation (scale + a tiny
 * shimmy), PWA install affordance. The auth splash is Ticker having an
 * anxious moment, not a spinner.
 *
 * Auth gate: session "unknown" → mascot + skeleton splash; "anon" → redirect
 * to the landing. Route-level lifecycle guards (banned → S-31, cooldown →
 * S-26 …) are VERTICAL-owned (V1/V2) and layer on top of this frame — the
 * foundation gate only guarantees "authenticated".
 */
import { useEffect } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { resolveCopy } from "@humble/shared";
import {
  AmbientBackground,
  GrainOverlay,
  IconChat,
  IconDeck,
  IconInstall,
  IconLikes,
  IconProfile,
  IconStore,
  Mascot,
  RouteTransition,
  Skeleton,
  springs,
} from "../../design/index.js";
import { ws } from "../../lib/ws.js";
import { sessionStore, uiStore, useStore } from "../../lib/store.js";
import { IconMenu } from "../menu/menu-icon.js";

const TABS = [
  { to: "/deck", label: "Deck", Icon: IconDeck },
  { to: "/menu", label: "Menu", Icon: IconMenu }, // S-14 Today's Menu — V7
  { to: "/likes", label: "Likes", Icon: IconLikes },
  { to: "/matches", label: "Chat", Icon: IconChat },
  { to: "/store", label: "Store", Icon: IconStore },
  { to: "/me", label: "You", Icon: IconProfile },
] as const;

export function TabFrame() {
  const status = useStore(sessionStore, (s) => s.status);
  const online = useStore(uiStore, (s) => s.online);
  const installPrompt = useStore(uiStore, (s) => s.installPrompt);
  const location = useLocation();

  // realtime plane follows the authenticated frame (idempotent)
  useEffect(() => {
    if (status === "authed") ws.ensureConnected();
  }, [status]);

  if (status === "unknown") {
    return (
      <div className="frame__main" style={{ paddingTop: 48 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <Mascot mood="loading" />
        </div>
        <Skeleton height={44} radius={12} style={{ marginBottom: 12 }} />
        <Skeleton height={340} radius={22} style={{ marginBottom: 12 }} />
        <Skeleton width="60%" />
      </div>
    );
  }
  if (status === "anon") {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="frame">
      {/* the shared atmosphere — every screen gets the warm, cosy treatment */}
      <AmbientBackground />
      <GrainOverlay />
      {!online && <div className="banner banner--offline">{resolveCopy("errors.offline")}</div>}
      <header className="frame__topbar">
        <div className="frame__topbar-inner">
          {/* wordmark only (v6): Ticker is the single brand heart */}
          <Link to="/deck" className="frame__brand">
            HUMBLE
          </Link>
          {installPrompt && (
            <motion.button
              type="button"
              className="btn btn--sm btn--ghost"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              whileTap={{ scale: 0.95 }}
              transition={springs.snappy}
              onClick={() => {
                void installPrompt.prompt();
                void installPrompt.userChoice.finally(() =>
                  uiStore.set({ installPrompt: null }),
                );
              }}
            >
              <IconInstall width={16} height={16} /> Install
            </motion.button>
          )}
        </div>
      </header>
      <main className="frame__main">
        <RouteTransition transitionKey={location.pathname}>
          <Outlet />
        </RouteTransition>
      </main>
      <nav className="tabbar" aria-label="Main">
        {TABS.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `tabbar__tab${isActive ? " tabbar__tab--active" : ""}`}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    className="tabbar__pill"
                    layoutId="tabbar-pill"
                    transition={springs.snappy}
                    aria-hidden="true"
                  />
                )}
                <motion.span
                  style={{ display: "flex" }}
                  animate={{
                    scale: isActive ? 1.14 : 1,
                    y: isActive ? -2 : 0,
                    rotate: isActive ? [0, -8, 6, 0] : 0,
                  }}
                  transition={{
                    ...springs.jelly,
                    rotate: { duration: 0.45, ease: "easeInOut" },
                  }}
                >
                  <Icon />
                </motion.span>
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
